<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.landlord_name')"
                v-model="filterOptions.name"
              />

              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.landlord_mobile')"
                v-model="filterOptions.mobile"
              />

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from_date')"
                v-model.trim="filterOptions.from"
              />
              <!-- End:: Start Date Input -->

              <!-- Start:: End Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to_date')"
                v-model.trim="filterOptions.to"
              />
              <!-- End:: End Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("PLACEHOLDERS.subscriptions_financial_reports") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadPdf"
            :disabled="pdfDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>
          <base-button
            class="mt-0 excel_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadExcel')"
            @fireClick="downloadExcel"
            :disabled="excelDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:[`item.serial`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.landlord_id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>

        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show_details") }}</span>
              </template>
              <button class="btn_show" @click="showLandlordDetails(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!-- ==== Start:: Overall Statistics ==== -->
      <div class="table_content mt-5">
        <v-simple-table class="stat-table">
          <template v-slot:default>
            <thead class="all-stat">
              <tr>
                <th class="text-center" style="color: white !important">
                  {{ $t("PLACEHOLDERS.total_revenue") }}
                </th>
                <th class="text-center" style="color: white !important">
                  {{ $t("PLACEHOLDERS.total_subscriptions_count") }}
                </th>
                <th class="text-center" style="color: white !important">
                  {{ $t("PLACEHOLDERS.average_subscription_value") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="text-center">
                <td>{{ totalRevenue }}</td>
                <td>{{ totalSubscriptionsCount }}</td>
                <td>{{ averageSubscriptionValue }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
      <!-- ==== End:: Overall Statistics ==== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->

    <!-- Start:: Landlord Details Modal -->
    <v-dialog v-model="landlordDetailsModalIsOpen" max-width="900px">
      <v-card>
        <v-card-title class="text-h5 justify-center">
          {{ $t("PLACEHOLDERS.subscription_details") }}
        </v-card-title>

        <v-card-text>
          <div class="landlord_details_wrapper" v-if="selectedLandlord">
            <div class="row mb-4">
              <div class="col-md-6">
                <h6>
                  <strong>{{ $t("PLACEHOLDERS.landlord_name") }}:</strong>
                  {{ selectedLandlord.landlord_name }}
                </h6>
              </div>
              <div class="col-md-6">
                <h6>
                  <strong>{{ $t("PLACEHOLDERS.landlord_mobile") }}:</strong>
                  {{ selectedLandlord.landlord_mobile }}
                </h6>
              </div>
            </div>

            <v-data-table
              class="thumb"
              :loading="loadingDetails"
              :loading-text="$t('TABLES.loadingData')"
              :headers="detailsTableHeaders"
              :items="landlordSubscriptions"
              item-class="ltr"
              hide-default-footer
            >
              <template v-slot:no-data>
                {{ $t("TABLES.noData") }}
              </template>

              <template v-slot:[`item.status`]="{ item }">
                <span
                  class="badge"
                  :class="
                    item.status === 'active' ? 'badge-success' : 'badge-danger'
                  "
                >
                  {{ item._status }}
                </span>
              </template>
            </v-data-table>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="modal_cancel_btn"
            @click="landlordDetailsModalIsOpen = false"
          >
            {{ $t("BUTTONS.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Landlord Details Modal -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllSubscriptionsFinancialReports",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    totalRevenue() {
      return this.tableRows
        .reduce(
          (sum, item) => sum + parseFloat(item.total_subscriptions_amount || 0),
          0
        )
        .toFixed(2);
    },

    totalSubscriptionsCount() {
      return this.tableRows.reduce(
        (sum, item) => sum + parseInt(item.packages_count || 0),
        0
      );
    },

    averageSubscriptionValue() {
      if (this.totalSubscriptionsCount === 0) return 0;
      return (this.totalRevenue / this.totalSubscriptionsCount).toFixed(2);
    },
  },

  data() {
    return {
      loading: false,
      loadingDetails: false,
      isWaitingRequest: false,
      excelDownloadBtnIsLoading: false,
      pdfDownloadBtnIsLoading: false,

      filterFormIsActive: false,
      searchValue: "",
      filterOptions: {
        name: null,
        mobile: null,
        from: null,
        to: null,
      },

      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "serial",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.landlord_name"),
          value: "landlord_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.landlord_mobile"),
          value: "landlord_mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.packages_count"),
          value: "packages_count",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.total_subscriptions_amount"),
          value: "total_subscriptions_amount",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.last_subscription_date"),
          value: "last_subscription_date",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ],

      detailsTableHeaders: [
        {
          text: this.$t("PLACEHOLDERS.package_name"),
          value: "package.name_ar",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_date"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.ads_number"),
          value: "ads_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.amount_paid"),
          value: "price",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_status"),
          value: "status",
          align: "center",
          sortable: false,
        },
      ],

      tableRows: [],
      landlordSubscriptions: [],
      selectedLandlord: null,
      landlordDetailsModalIsOpen: false,

      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
    };
  },

  methods: {
    async submitFilterForm() {
      this.paginations.current_page = 1;
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.mobile = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;
      this.paginations.current_page = 1;
      this.setTableRows();
    },

    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "subscriptions/financial-reports",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.name,
            mobile: this.filterOptions.mobile,
            from: this.filterOptions.from,
            to: this.filterOptions.to,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data;
        this.paginations.last_page = res.data.meta?.last_page || 1;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(this.$t("MESSAGES.somethingWentWrong"));
      }
    },

    async showLandlordDetails(landlord) {
      console.log("Landlord clicked:", landlord);

      if (!landlord || !landlord.landlord_id) {
        this.$message.warning(this.$t("MESSAGES.somethingWentWrong"));
        return;
      }

      this.selectedLandlord = landlord;
      this.landlordDetailsModalIsOpen = true;
      this.loadingDetails = true;

      try {
        let res = await this.$axios({
          method: "GET",
          url: `clients/subscriptions/${landlord.landlord_id}`,
          params: {
            from: this.filterOptions.from,
            to: this.filterOptions.to,
          }
        });
        this.loadingDetails = false;
        this.landlordSubscriptions = res.data.data;
        console.log("Subscriptions loaded:", this.landlordSubscriptions);
      } catch (error) {
        this.loadingDetails = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(this.$t("MESSAGES.somethingWentWrong"));
      }
    },

    async downloadPdf() {
      this.pdfDownloadBtnIsLoading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "subscriptions/financial-reports/export-pdf",
          params: {
            name: this.filterOptions.name,
            mobile: this.filterOptions.mobile,
            from: this.filterOptions.from,
            to: this.filterOptions.to,
          },
        });
        this.pdfDownloadBtnIsLoading = false;

        if (res.data.data && res.data.data.url) {
          window.open(res.data.data.url, "_blank");
          this.$message.success(this.$t("MESSAGES.downloadedSuccessfully"));
        }
      } catch (error) {
        this.pdfDownloadBtnIsLoading = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(this.$t("MESSAGES.somethingWentWrong"));
      }
    },

    async downloadExcel() {
      this.excelDownloadBtnIsLoading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "subscriptions/financial-reports/export-excel",
          params: {
            name: this.filterOptions.name,
            mobile: this.filterOptions.mobile,
            from: this.filterOptions.from,
            to: this.filterOptions.to,
          },
        });
        this.excelDownloadBtnIsLoading = false;

        if (res.data.data && res.data.data.url) {
          window.open(res.data.data.url, "_blank");
          this.$message.success(this.$t("MESSAGES.downloadedSuccessfully"));
        }
      } catch (error) {
        this.excelDownloadBtnIsLoading = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(this.$t("MESSAGES.somethingWentWrong"));
      }
    },

    updateRouterQueryParam(pageNumber) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pageNumber,
        },
      });
      this.setTableRows();
    },
  },

  created() {
    // Get page from query params if exists
    if (this.$route.query.page) {
      this.paginations.current_page = parseInt(this.$route.query.page);
    }
    this.setTableRows();
  },
};
</script>

<style scoped>
.all-stat {
  background: var(--main_theme_clr);
}

.all-stat th {
  color: white !important;
  font-size: 16px !important;
  font-weight: 800;
}

.badge {
  padding: 5px 15px;
  border-radius: 5px;
  font-weight: bold;
}

.badge-success {
  background: #28a745;
  color: white;
}

.badge-danger {
  background: #dc3545;
  color: white;
}

.landlord_details_wrapper h6 {
  margin-bottom: 10px;
}
</style>
